import { ORG_ROUTE_PATHS } from "@/components/organization/orgRoutes";
import { AGENT_SETTINGS_PATHS } from "@/components/build/agentSettingsRoutes";

export const NAV_OPEN_STORAGE_KEY = "agent-os.shell-nav-open";

export type AgentTreeGroup =
  | "studio"
  | "spec"
  | "workflows"
  | "miniapps"
  | "config";

export type NavOpenState = {
  appsOpen: boolean;
  agentsOpen: boolean;
  workOpen: boolean;
  orgOpen: boolean;
  openAppKeys: Record<string, boolean>;
  openAgentIds: Record<string, boolean>;
  openAgentGroups: Record<string, boolean>;
};

export const AGENT_GROUP_DEFAULTS: Record<AgentTreeGroup, boolean> = {
  studio: true,
  spec: true,
  workflows: false,
  miniapps: false,
  config: false,
};

/** One level: 16px — readable nest without pushing the sidebar. */
export const TREE_CHILD_INDENT_CLASS = "space-y-0.5 pl-4";

/** Highlight only the destination leaf — never its ancestors. */
export function isTreeLeafCurrent(input: {
  selfIsDestination: boolean;
  descendantIsDestination?: boolean;
}): boolean {
  return input.selfIsDestination && !input.descendantIsDestination;
}

export function agentGroupKey(
  agentId: string,
  group: AgentTreeGroup,
): string {
  return `${agentId}:${group}`;
}

export function isAgentGroupOpen(
  openAgentGroups: Record<string, boolean>,
  agentId: string,
  group: AgentTreeGroup,
): boolean {
  return (
    openAgentGroups[agentGroupKey(agentId, group)] ??
    AGENT_GROUP_DEFAULTS[group]
  );
}

const emptyNavOpenState = (): NavOpenState => ({
  appsOpen: true,
  agentsOpen: true,
  workOpen: true,
  orgOpen: true,
  openAppKeys: {},
  openAgentIds: {},
  openAgentGroups: {},
});

export function readNavOpenState(): NavOpenState {
  try {
    const raw = localStorage.getItem(NAV_OPEN_STORAGE_KEY);
    if (!raw) {
      return emptyNavOpenState();
    }
    const parsed = JSON.parse(raw) as Partial<NavOpenState>;
    return {
      appsOpen: parsed.appsOpen ?? true,
      agentsOpen: parsed.agentsOpen ?? true,
      workOpen: parsed.workOpen ?? true,
      orgOpen: parsed.orgOpen ?? true,
      openAppKeys: parsed.openAppKeys ?? {},
      openAgentIds: parsed.openAgentIds ?? {},
      openAgentGroups: parsed.openAgentGroups ?? {},
    };
  } catch {
    return emptyNavOpenState();
  }
}

export function writeNavOpenState(state: NavOpenState) {
  localStorage.setItem(NAV_OPEN_STORAGE_KEY, JSON.stringify(state));
}

export function revealAgentGroups(
  current: Record<string, boolean>,
  agentId: string,
  groups: AgentTreeGroup[],
): Record<string, boolean> {
  let next = current;
  for (const group of groups) {
    const key = agentGroupKey(agentId, group);
    if (!current[key]) {
      if (next === current) {
        next = { ...current };
      }
      next[key] = true;
    }
  }
  return next;
}

export function groupsToReveal(input: {
  pathname: string;
  activeSection: string | null;
}): AgentTreeGroup[] {
  const groups: AgentTreeGroup[] = [];
  if (input.pathname.startsWith("/studio/chat")) {
    groups.push("studio");
  }
  if (input.pathname.startsWith("/studio/agent-spec")) {
    groups.push("spec");
    if (input.activeSection === "workflows") {
      groups.push("workflows");
    }
    if (input.activeSection === "miniapps") {
      groups.push("miniapps");
    }
  }
  // Every Agent settings leaf lives in the Settings folder (lot R4);
  // Application leaves (Access, Channels, Web) and Organisation leaves never
  // open an Agent group.
  if (isAgentSettingsPath(input.pathname)) {
    groups.push("config");
  }
  return groups;
}

/** Studio › Organisation leaves are org-level: they never open an Agent group. */
export function isOrganizationPath(pathname: string): boolean {
  return ORG_ROUTE_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/** Studio › Agents › Agent › Settings leaves (Tools … Spaces). */
export function isAgentSettingsPath(pathname: string): boolean {
  return AGENT_SETTINGS_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}
